import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useSetSnackbarGlobalError, useSetSnackbarMessage } from '@tempo-io/fim3-components';
import type {
  ChangeDemandStatusResponseDto,
  CreateDemandAttachmentDto,
  CreateDemandCommentDto,
  CreateDemandDto,
  CreateDemandResponseDto,
  DemandCommentDto,
  DemandFilterSchemaDto,
  DemandLinkedRoadmapItemDto,
  GetAllDemandsDto,
  GetDemandAttachmentResponseDto,
  GetDemandAttachmentsResponseDto,
  GetDemandByIdResponseDto,
  GetDemandCommentResponseDto,
  GetDemandsResponseDto,
  MatrixDemandSummaryDto,
  UpdateDemandCommentDto,
  UpdateDemandDto,
  UpdateDemandResponseDto,
} from '@tempo-io/fim3-dto';
import { DescriptionMaxLength } from '@tempo-io/fim3-dto';
import type { AxiosInstance } from 'axios';
import { useTranslation } from 'react-i18next';
import { demandsQueryKeys } from '@/lib/query-keys/demands';
import { isDescriptionTooLongError } from './description-error.utils';
import { useErrorHandlingMutation } from './useErrorHandlingMutation';
import { getUserDataForResults } from './useFim3Api';
import { useS3Api } from './useS3Api';

export type DemandQueryParams = GetAllDemandsDto & {
  filter?: DemandFilterSchemaDto;
  matchingDemandIds?: number[];
};

export const useDemandsApi = (httpClient: AxiosInstance) => {
  const baseUrl = `/demands`;
  const s3Api = useS3Api(httpClient);
  const defaultLimit = 25;

  const getDemands = async (params: DemandQueryParams): Promise<GetDemandsResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/getDemands`, params);
    return data;
  };

  const changeDemandStatus = async (
    id: number,
    statusId: number,
  ): Promise<ChangeDemandStatusResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}/status`, { statusId });
    return data;
  };

  const getDemand = async (id: number): Promise<GetDemandByIdResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}`);
    return data;
  };

  const createDemand = async (demand: CreateDemandDto): Promise<CreateDemandResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}`, demand);
    return data;
  };

  const updateDemand = async (
    id: number,
    demand: UpdateDemandDto,
  ): Promise<UpdateDemandResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${id}`, demand);
    return data;
  };

  const deleteDemand = async (id: number) => {
    const { data } = await httpClient.delete(`${baseUrl}/${id}`);
    return { data };
  };

  const getDemandRoadmapItems = async (id: number): Promise<string[]> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/roadmap-items`);
    return [...data];
  };

  const linkDemandRoadmapItem = async (
    linkedItem: DemandLinkedRoadmapItemDto,
  ): Promise<DemandLinkedRoadmapItemDto> => {
    const { demandId, ...rest } = linkedItem;
    const { data } = await httpClient.post(`${baseUrl}/${demandId}/roadmap-items`, rest);
    return data;
  };

  const unlinkDemandRoadmapItem = async (params: { demandId: number; roadmapItemId: string }) => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.demandId}/roadmap-items/${params.roadmapItemId}`,
    );
    return data;
  };

  const getDemandComments = async (demandId: number) => {
    const { data } = await httpClient.get(`${baseUrl}/${demandId}/comments`);

    const userIds = data.data.map((el: DemandCommentDto) => el.commenterId);

    return getUserDataForResults(
      httpClient,
      userIds,
      data.data,
      (item: DemandCommentDto) => item.commenterId ?? '',
    );
  };

  const createDemandComment = async (
    comment: CreateDemandCommentDto & { demandId: number },
  ): Promise<GetDemandCommentResponseDto> => {
    const { data } = await httpClient.post(`${baseUrl}/${comment.demandId}/comments`, {
      comment: comment.comment,
    });
    return data;
  };

  const updateDemandComment = async (
    comment: UpdateDemandCommentDto & { id: number; demandId: number },
  ): Promise<GetDemandCommentResponseDto> => {
    const { data } = await httpClient.put(`${baseUrl}/${comment.demandId}/comments/${comment.id}`, {
      comment: comment.comment,
    });
    return data;
  };

  const deleteDemandComment = async (params: {
    id: number;
    demandId: number;
  }): Promise<GetDemandCommentResponseDto> => {
    const { data } = await httpClient.delete(`${baseUrl}/${params.demandId}/comment/${params.id}`);
    return data;
  };

  const getDemandAttachments = async (id: number): Promise<GetDemandAttachmentsResponseDto> => {
    const { data } = await httpClient.get(`${baseUrl}/${id}/attachments`);
    return data;
  };

  const createDemandAttachment = async ({
    file,
    attachment,
  }: {
    file: File;
    attachment: CreateDemandAttachmentDto;
  }): Promise<GetDemandAttachmentResponseDto> => {
    const key = await s3Api.uploadAttachment.mutateAsync(file);

    const { data } = await httpClient.post(`${baseUrl}/${attachment.demandId}/attachments`, {
      ...attachment,
      attachmentUrl: key,
    });

    return data;
  };

  const removeDemandAttachment = async (params: { demandId: number; id: number }) => {
    const { data } = await httpClient.delete(
      `${baseUrl}/${params.demandId}/attachments/${params.id}`,
    );
    return { data };
  };

  const useGetAll = (params: DemandQueryParams = {}, options: { enabled?: boolean } = {}) => {
    const limit = params.limit ?? defaultLimit;
    const normalizedParams = { ...params, limit };
    return useInfiniteQuery({
      initialPageParam: 0,
      gcTime: 0,
      enabled: options.enabled ?? true,
      queryKey: demandsQueryKeys.list(normalizedParams),
      queryFn: ({ pageParam }) => getDemands({ ...params, limit, offset: pageParam * limit }),
      getNextPageParam: (lastPage) =>
        lastPage.data.length === limit ? lastPage.pagination.offset / limit + 1 : undefined,
    });
  };

  const getMatrixDemands = async ({ params }: { params?: unknown }) => {
    const { data } = await httpClient.post<{
      data: (MatrixDemandSummaryDto & { id: number })[];
    }>(`${baseUrl}/getMatrixDemands`, params);

    return data;
  };

  const useMatrixDemands = (
    viewId?: number,
    params: {
      filter?: DemandFilterSchemaDto;
      search?: string;
      matchingDemandIds?: number[];
    } = {},
    enabled = false,
  ) => {
    const queryKey: QueryKey = [...demandsQueryKeys.matrix(), baseUrl, viewId ?? null, params];

    return useQuery({
      queryKey,
      queryFn: () => getMatrixDemands({ params: { ...params, viewId } }),
      enabled,
    });
  };

  const useGetDemand = (id?: number | null) =>
    useQuery({
      queryKey: demandsQueryKeys.detail(id ?? 0),
      queryFn: () => getDemand(id ?? 0),
      enabled: typeof id === 'number' && id > 0,
    });

  const useGetDemandRoadmapItems = (id: number) =>
    useQuery({
      queryKey: [baseUrl, id, 'roadmap-items'],
      queryFn: () => getDemandRoadmapItems(id),
      enabled: id > 0,
    });

  const useDemandComments = ({ id }: { id: number }) =>
    useQuery({
      queryKey: demandsQueryKeys.comments(id),
      queryFn: () => getDemandComments(id),
      enabled: id > 0,
    });

  const useDemandAttachments = (id: number) =>
    useQuery({
      queryKey: demandsQueryKeys.attachments(id),
      queryFn: () => getDemandAttachments(id),
      enabled: id > 0,
    });

  const queryClient = useQueryClient();
  const setSnackbarGlobalError = useSetSnackbarGlobalError();
  const setSnackbarMessage = useSetSnackbarMessage();
  const { t } = useTranslation('translation');

  const useChangeStatus = () =>
    useErrorHandlingMutation<
      ChangeDemandStatusResponseDto,
      unknown,
      { id: number; statusId: number },
      { previous: [readonly unknown[], unknown][] }
    >({
      mutationFn: ({ id, statusId }) => changeDemandStatus(id, statusId),
      onMutate: async ({ id, statusId }) => {
        await queryClient.cancelQueries({ queryKey: demandsQueryKeys.lists() });
        const previous = queryClient.getQueriesData({ queryKey: demandsQueryKeys.lists() });
        queryClient.setQueriesData<InfiniteData<GetDemandsResponseDto>>(
          { queryKey: demandsQueryKeys.lists() },
          (old) =>
            old
              ? {
                  ...old,
                  pages: old.pages.map((page) => ({
                    ...page,
                    data: page.data.map((demand) =>
                      demand.id === id ? { ...demand, statusId } : demand,
                    ),
                  })),
                }
              : old,
        );
        return { previous };
      },
      onError: (error, variables, context) => {
        for (const [key, data] of context?.previous ?? []) {
          queryClient.setQueryData(key, data);
        }
        setSnackbarGlobalError(error, variables);
      },
      onSettled: () =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() }),
        ]),
    });

  const invalidateDemandsAndMatrix = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all() }),
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() }),
    ]);

  const useCreate = () =>
    useErrorHandlingMutation({
      mutationFn: createDemand,
      onSuccess: invalidateDemandsAndMatrix,
      onError: (error: unknown, variables) => {
        if (isDescriptionTooLongError(error)) {
          setSnackbarMessage({
            message: t(($) => $.ideaManagement.errors.descriptionTooLong, {
              max: DescriptionMaxLength.toLocaleString(),
            }),
            severity: 'error',
          });
          return;
        }
        setSnackbarGlobalError(error, variables);
      },
    });

  const useUpdate = () =>
    useErrorHandlingMutation({
      mutationFn: ({ id, demand }: { id: number; demand: UpdateDemandDto }) =>
        updateDemand(id, demand),
      onMutate: async ({ id, demand }) => {
        await queryClient.cancelQueries({ queryKey: demandsQueryKeys.detail(id) });
        const previous = queryClient.getQueryData<GetDemandByIdResponseDto>(
          demandsQueryKeys.detail(id),
        );
        if (previous?.data) {
          queryClient.setQueryData<GetDemandByIdResponseDto>(demandsQueryKeys.detail(id), {
            data: { ...previous.data, ...demand },
          });
        }
        return { previous };
      },
      onError: (error, variables, context) => {
        if (context?.previous) {
          queryClient.setQueryData(demandsQueryKeys.detail(variables.id), context.previous);
        }

        if (isDescriptionTooLongError(error)) {
          setSnackbarMessage({
            message: t(($) => $.ideaManagement.errors.descriptionTooLong, {
              max: DescriptionMaxLength.toLocaleString(),
            }),
            severity: 'error',
          });
          return;
        }

        setSnackbarGlobalError(error, variables);
      },
      onSettled: (_data, _error, variables) =>
        Promise.all([
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.matrix() }),
          queryClient.invalidateQueries({ queryKey: demandsQueryKeys.detail(variables.id) }),
        ]),
    });

  const useDelete = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemand,
      onSuccess: invalidateDemandsAndMatrix,
    });

  const linkRoadmapItem = useErrorHandlingMutation({
    mutationFn: linkDemandRoadmapItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all() });
      queryClient.invalidateQueries({ queryKey: [baseUrl, variables.demandId, 'roadmap-items'] });
      queryClient.invalidateQueries({
        queryKey: [baseUrl, variables.demandId, 'roadmap-items', 'details'],
      });
    },
  });

  const unlinkRoadmapItem = useErrorHandlingMutation({
    mutationFn: unlinkDemandRoadmapItem,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: demandsQueryKeys.all() });
      queryClient.invalidateQueries({ queryKey: [baseUrl, variables.demandId, 'roadmap-items'] });
      queryClient.invalidateQueries({
        queryKey: [baseUrl, variables.demandId, 'roadmap-items', 'details'],
      });
    },
  });

  const invalidateComments = (demandId: number) =>
    queryClient.invalidateQueries({ queryKey: demandsQueryKeys.comments(demandId) });

  const useCreateComment = () =>
    useErrorHandlingMutation({
      mutationFn: createDemandComment,
      onSuccess: (_data, variables) => invalidateComments(variables.demandId),
    });

  const useUpdateComment = () =>
    useErrorHandlingMutation({
      mutationFn: updateDemandComment,
      onSuccess: (_data, variables) => invalidateComments(variables.demandId),
    });

  const useDeleteComment = () =>
    useErrorHandlingMutation({
      mutationFn: deleteDemandComment,
      onSuccess: (_data, variables) => invalidateComments(variables.demandId),
    });

  const invalidateAttachments = (demandId: number) =>
    queryClient.invalidateQueries({ queryKey: demandsQueryKeys.attachments(demandId) });

  const createAttachment = useErrorHandlingMutation({
    mutationFn: createDemandAttachment,
    onSuccess: (_data, variables) => invalidateAttachments(variables.attachment.demandId),
  });

  const removeAttachment = useErrorHandlingMutation({
    mutationFn: removeDemandAttachment,
    onSuccess: (_data, variables) => invalidateAttachments(variables.demandId),
  });

  return {
    getDemands,
    useGetAll,
    useMatrixDemands,
    useGetDemand,
    useGetDemandRoadmapItems,
    useDemandComments,
    useDemandAttachments,
    useChangeStatus,
    useCreate,
    useUpdate,
    useDelete,
    linkRoadmapItem,
    unlinkRoadmapItem,
    useCreateComment,
    useUpdateComment,
    useDeleteComment,
    createAttachment,
    removeAttachment,
  };
};
